import React, { Fragment, memo, useRef, useState } from 'react'
import ChildLink from './childLink'
import ChildModal from './childModal'
import ChildAccordion from './childAccordion'
import { Label } from '../../../components'
import { InferLabelFromName } from '../../../utils'
import shallowEqual from 'shallowequal'
import RemoveMarkdown from 'remove-markdown'
import ChildInline from './childInline'

function ChildObject (props) {
  const {
    id,
    name,
    label,
    link,
    selFrom,
    index,
    context,
    helpText,
    activate,
    topRecordId,
    inLinkedObj,
    style,
    getSummaryOverrideFn,
  } = props
  const inList = context.isList
  const [visible, setVisible] = useState((activate && inList) || false)
  const linkButton = useRef(null)
  const childState = inList
    ? context.getChildContext(index)
    : context.getChildContext(name)

  const handleObjectChange = (key, val) => {
    if (!childState.mutable) {
      return
    }
    childState.mutateVarData(key, val)
  }
  const showModal = () => {
    if (childState.mutable && !childState.hasData) {
      context.mutateVarData(inList ? index : name,
        childState.scope && childState.scope.value)
    }
    setVisible(true)
  }
  const switchModal = () => { // switch accordion panes
    if (childState.mutable && !childState.hasData) {
      context.mutateVarData(inList ? index : name,
        childState.scope && childState.scope.value)
    }
    setVisible(!visible)
  }
  const hideModal = () => {
    if (!inList) { // 2021-03-18: LS changed this to it no longer sets objects in lists to null when you close the modal
      // it was causing a problem where the id would get deleted but not re-created until you answered something,
      // which in turn was causing some kind of caching issue that I never fully figured out.
      // I believe, though, that the "set to null when you leave" was primarily (perhaps ONLY) important for
      // objects NOT in lists, because when items are in lists, you have a button in the UI to delete them,
      // which is not the case for objects that are NOT in lists.
      if (childState.mutable && !childState.hasData) {
        context.mutateVarData(inList ? index : name, null)
      }
    }
    setVisible(false)
  }
  const handleClose = () => {
    // if this ChildObject is for a mutable hybrid selection...
    if (childState.mutable) {
      const linkObj = childState.getQueryLink()
      if (linkObj && linkObj.linkType === 'select') {
        // todo: compare the current data state against the cached value to check for modifications.
        childState.getQueryStatus(linkObj.linkExpr)
        // debugger
        // If there are modifications, show a modal popup asking whether to save the changes or revert them.
        // If they choose to save, save the changes to the query & update the cached value;
        // otherwise re-select the cached value!
      }
    }
    // refreshStatus()
    hideModal()
    linkButton.current.focus()
  }

  const typeDef = childState.typeDef
  const linkId = id + '|link'
  const status = childState.getStatus()
  const hasData = childState.hasData
  const text = getSummaryOverrideFn
    ? getSummaryOverrideFn(childState.scope, childState.scope)
    : hasData ? childState.getSummary() : ''
  if (style === 'popup') {
    const renderedChild = (
      <Fragment key={id}>
        {inList ? '' : <Label name={name} label={label} helpText={helpText}
                              forId={linkId}/>}
        <ChildLink id={linkId}
                   index={index}
                   text={text}
                   status={status}
                   hasData={hasData}
                   activate={activate}
                   showModal={showModal}
                   ref={linkButton}
                   readOnly={!childState.mutable}
        />
        <ChildModal id={id} name={name}
                    title={label ? RemoveMarkdown(label) : InferLabelFromName(
                      name)}
                    isShow={visible} selFrom={selFrom}
                    link={link} index={index} context={childState}
                    typeDef={typeDef} topRecordId={topRecordId}
                    inLinkedObj={inLinkedObj}
                    change={handleObjectChange} handleClose={handleClose}/>
      </Fragment>
    )
    // childDataState.reset()
    return renderedChild
  }
  if (style === 'inline') {
    return (<Fragment key={id}>
      {inList ? '' : <Label name={name} label={label} helpText={helpText}
                            forId={linkId}/>}
      {inList && (<label className={'mr-2 my-2 d-block'}>{index + 1}</label>)}
      <ChildInline id={id}
                   name={name}
                   activate={activate}
                   selFrom={selFrom}
                   link={link}
                   index={index}
                   status={status}
                   context={childState}
                   typeDef={typeDef}
                   topRecordId={topRecordId}
                   inLinkedObj={inLinkedObj}
                   change={handleObjectChange}
      />
    </Fragment>)
  }
  if (style === 'accordion') {
    if (inList) {
      return (<Fragment key={id}>
        <div className={'d-flex flex-column w-100'}
             style={{ marginBottom: '0.75rem' }}>
          <ChildLink id={linkId}
                     index={index}
                     text={text}
                     status={status}
                     hasData={hasData}
                     activate={activate}
                     showModal={switchModal}
                     ref={linkButton}
                     isTurn={visible}
          />
          <ChildAccordion id={id} name={name}
                          title={label
                            ? RemoveMarkdown(label)
                            : InferLabelFromName(
                              name)}
                          isShow={visible} selFrom={selFrom}
                          link={link} index={index} context={childState}
                          typeDef={typeDef}
                          topRecordId={topRecordId}
                          inLinkedObj={inLinkedObj}
                          change={handleObjectChange} handleOk={handleClose}
                          handleCancel={handleClose}/>
        </div>
      </Fragment>)
    }
    const renderedChild = (<Fragment key={id}>
      {inList ? '' : <Label name={name} label={label} helpText={helpText}
                            forId={linkId}/>}
      <ChildLink id={linkId}
                 index={index}
                 text={text}
                 status={status}
                 hasData={hasData}
                 activate={activate}
                 showModal={switchModal}
                 ref={linkButton}
                 isTurn={visible}
      />
      <ChildAccordion id={id} name={name}
                      title={label ? RemoveMarkdown(label) : InferLabelFromName(
                        name)}
                      isShow={visible} selFrom={selFrom}
                      link={link} index={index} context={childState}
                      typeDef={typeDef}
                      topRecordId={topRecordId}
                      inLinkedObj={inLinkedObj}
                      change={handleObjectChange} handleOk={handleClose}
                      handleCancel={handleClose}/>
    </Fragment>)
    return renderedChild
  }
  return null
}

function areEqual (prevProps, nextProps) {
  /*
  return true if passing nextProps to render would return
  the same result as passing prevProps to render,
  otherwise return false
  */
  // const { name, index, context } = nextProps
  // const inList = (typeof index === 'number' && index >= 0)
  // let childDataState = context.getChildContext(name)
  // if (inList) {
  //   childDataState = childDataState.getChildContext(index)
  // }
  // if (childDataState.dirty) {
  //   return false
  // }
  // fall-back
  return shallowEqual(prevProps, nextProps)
}

export default memo(ChildObject, areEqual)
